/*
 * Copyright (c) 2022. Nedaware SL
 */

import {Resource} from '../../api/resource';
import {EntityLoader} from '../../api/entity-loader';
import {BasicEntityInterface} from '../basic-entity-interface/basic-entity-interface';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {TempUri, Uri} from '../../api/uri';
import {first, map, shareReplay, startWith, tap} from 'rxjs/operators';
import {ManagerCachingStrategy} from '../basic-entity-interface/mapping-external';
import {TokenService} from '../../api/session/token.service';
import {HttpErrorResponse} from '@angular/common/http';
import {FilterList} from "../../api/filter-list";

/**
 * Nedaware S.L.
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */

/**
 * A manager to manage a concrete model of the system,
 * which can cache all the items of a model when requested
 * and then return them from the cache instead of the network.
 * When updating the items, we should do it using the manager so it
 * is updated correctly.
 * The manager can also work transparently if no precaching should be used.
 * Should not be instantiated manually but through the interfaces service
 * to ensure a unique instance per model.
 */
export class EntityManager<T extends Resource> {
    private _cache = new Map<string, T>();
    private _changes = new Subject<void>();
    private _loadingAll = false;
    private _loaded = new BehaviorSubject<boolean>(false);
    private _all: Observable<T[]> = null;
    /** To get notifications about when all the items are loaded */
    public readonly loaded$: Observable<boolean> = this._loaded.asObservable();

    constructor(public loader: EntityLoader<T, BasicEntityInterface<T>>, private _session: TokenService) {

    }

    /**
     * Will only work as expected if we preload/reload at some point
     */
    public get all(): Observable<T[]> {
        if (!this._all) {
            this.preload();
            this._all = this._changes.pipe(
                startWith([null]),
                map(() => Array.from(this._cache.values())),
                shareReplay(1)
            );
        }
        return this._all;
    }

    /**
     * Obtains the current total of elements in cache
     */
    public get allNow(): T[] {
        return Array.from(this._cache.values());
    }

    /**
     * Preloads ALL the possible entities for this
     */
    public preload() {
        if (!this.loaded) {
            this._loadAll();
        }
    }

    public reload(filters?: FilterList) {
        this._loadAll(false, filters);
    }

    public getById(...id: string[]): Promise<T> {
        const pattern = this.loader.entityInterface.uriPattern;
        return this.get(new Uri(id, pattern, this.loader.entityInterface.serialiser.model).toString());
    }

    public get(iri: string): Promise<T> {
        // Creating it already we check it is not a temporal IRI
        const uri = Uri.fromUriStr(iri, this.loader.entityInterface.uriPattern, this.loader.entityInterface.serialiser.model);
        return new Promise((resolve, reject) => {
            if (this.loader.entityInterface.managerCaching) {
                if (this._cache.has(iri)) {
                    resolve(this._cache.get(iri));
                } else {
                    this._waitForIt(iri).then(
                        v => resolve(v),
                        err => reject(err));
                }
            } else {
                return this._load(uri).subscribe(t => resolve(t), err => reject(err));
            }
        });
    }

    public hasCached(iri: string): boolean {
        return this._cache.has(iri);
    }

    public getFromCache(iri: string): T | undefined {
        return this._cache.get(iri);
    }

    private _waitForIt(iri: string): Promise<T> {
        return new Promise((resolve, reject) => {
            if (!this.loaded) {
                this._loadAll();
            }
            if (this._loadingAll) {
                this._changes
                    .pipe(first(() => this._cache.has(iri) || !this._loadingAll))
                    .subscribe(() => {
                        if (this._cache.has(iri)) {
                            resolve(this._cache.get(iri));
                        } else {
                            reject('not found');
                        }
                    });
            } else {
                reject('not found');
            }
        });
    }

    public update(entity: T): Observable<T> {
        if (!entity) {
            return;
        }

        if (entity.isTemporalEntity) {
            return this.add(entity);
        } else {
            return this.loader
                .update(entity)
                .pipe(tap(t => this._cacheSet(t)));
        }
    }

    public add(entity: T): Observable<T> {
        if (!entity) {
            return;
        }

        if (!entity.isTemporalEntity) {
            console.warn('Add requested, but it is not a temporal entity', entity);
            return this.update(entity);
        } else {
            return this.loader
                .add(entity)
                .pipe(tap(t => this._cacheSet(t)));
        }
    }

    public remove(entity: T) {
        if (!entity || entity.isTemporalEntity) {
            return;
        }

        const iri = entity.iri.toString();
        return this.loader
            .remove(entity)
            .pipe(tap(() => this._cacheRemove(iri)));
    }

    private _cacheSet(t: T) {
        if (this.loader.entityInterface.managerCaching) {
            this._cache.set(t.iri.toString(), t);
            this._changes.next();
        }
    }

    private _cacheRemove(iri: string) {
        if (this.loader.entityInterface.managerCaching) {
            this._cache.delete(iri);
            this._changes.next();
        }
    }

    private _load(iri: Uri) {
        if (iri instanceof TempUri) {
            throw new Error(`Cannot load a temporal uri (${iri.toString()})`);
        }
        return this.loader.findById(iri.id)
            .pipe(tap(t => this._cacheSet(t)));
    }

    private _loadAll(retrying = false, filters = []) {
        if (!retrying && (this._loadingAll || this.loader.entityInterface.managerCaching === ManagerCachingStrategy.NoCache)) {
            return;
        }

        this._loadingAll = true;
        this.loader
            .findAndFollow(filters)
            .subscribe(page => {
                page.member.forEach(t => {
                    this._cache.set(t.iri.toString(), t);
                });
                this._changes.next();
            }, this._onLoadingAllFailed.bind(this), () => {
                this._loaded.next(true);
                this._loadingAll = false;
            });
    }

    private _onLoadingAllFailed(err: HttpErrorResponse) {
        if (err.status === 401) {
            // 401 means not logged
            console.warn(`Error loading all for ${this.loader.entityInterface.name}, retrying after login.`);
            this._session.sessionChanges
                .pipe(first(() => this._session.checkLogin()))
                .subscribe(() => this._loadAll(true));
        } else if (err.status === 403) {
            // 403 is forbidden, so no point in retrying by now
            console.error(`Error loading all for ${this.loader.entityInterface.name}, giving up since it is forbidden.`);
            this._loadingAll = false;
            this._loaded.next(false);
        } else {
            console.log(err);
            console.warn(`Error loading all for ${this.loader.entityInterface.name}, retrying in 5s`);
            setTimeout(() => this._loadAll(true), 5000);
        }
    }

    public get loaded(): boolean {
        return this._loaded.getValue();
    }
}
